/**
 * What the employee's own screen needs to say.
 *
 * "Most of the 142 people never approve anything. They come to check a payslip,
 * book a week off, and find last year's tax document. This screen is
 * deliberately emptier than the admin dashboard, and warmer."
 *
 * That emptiness is the design, so the arithmetic here is deliberately narrow:
 * one person's pay, one person's leave, one person's claims. Nothing aggregates
 * across the company, because nothing on this screen is allowed to.
 */

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_MS = 86400000;

export const monthName = (i) => MONTHS[i] || "";

/** "6 April" — spelled from a table; ICU renders "Sept" for September. */
export const dayMonth = (value) => {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (m) return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

const utcDay = (value) => {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (m) return Date.parse(`${m[1]}-${m[2]}-${m[3]}T00:00:00.000Z`);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

export const greeting = (now = new Date()) => {
  const h = now.getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

/**
 * The pay card.
 *
 * The estimate is the last payslip's net, and it is labelled an estimate for
 * that reason — this month's has not been run, and quoting last month's figure
 * as though it were settled would be a promise the screen cannot keep.
 *
 * There is no pay-schedule the employee can read, so the date is stated only
 * when a payslip carries one. "Next payday" with no date is not a card.
 */
export const payOutlook = (payslips = []) => {
  /*
    Published only. A payslip exists from the moment the run is calculated, but
    it is not the employee's to see until payroll publishes it — showing an
    unpublished one puts a figure in front of somebody that may still change,
    and does it on the screen they trust most.
  */
  const sorted = [...(payslips || [])].filter((p) => p?.published).sort(
    (a, b) => (b.year - a.year) || (b.month - a.month)
  );
  const latest = sorted[0] || null;
  if (!latest) return null;
  return {
    estimate: Number(latest.netPay) || 0,
    // What the estimate is based on, so "estimated" is not a bare hedge.
    basedOn: `${monthName(latest.month)} ${latest.year}`,
    accountEnding: latest.accountEnding || null,
    latestId: latest._id,
    paid: latest.status === "paid" || Boolean(latest.payrollRun?.paymentDate),
  };
};

/**
 * The leave card: days left against entitlement, and what is about to expire.
 *
 * Balances arrive per leave type. The headline is the total, because "12.5 days
 * left of 24" is the sentence people actually hold in their heads, and the
 * per-type split is the detail underneath.
 */
export const leaveOutlook = (balances = []) => {
  const rows = (balances || []).filter(Boolean);
  if (!rows.length) return null;
  const left = rows.reduce((t, b) => t + (Number(b.quantity) || 0), 0);
  const total = rows.reduce(
    (t, b) => t + (Number(b.entitlement ?? b.total ?? b.allocated ?? 0) || 0),
    0
  );
  return {
    left: Math.round(left * 10) / 10,
    // Zero entitlement means we were not told, not that they have none.
    total: total > 0 ? Math.round(total * 10) / 10 : null,
    used: total > 0 ? Math.max(0, Math.round((total - left) * 10) / 10) : null,
    share: total > 0 ? Math.min(1, Math.max(0, left / total)) : null,
    types: rows.map((b) => ({ name: b.leaveType, left: Number(b.quantity) || 0 })),
  };
};

/** How long they have been clocked in, as "6h 14m". */
export const elapsedSince = (inTime, now = new Date()) => {
  const start = inTime ? new Date(inTime) : null;
  if (!start || Number.isNaN(start.getTime())) return null;
  const mins = Math.floor((now.getTime() - start.getTime()) / 60000);
  if (mins < 0) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
};

/**
 * The claims card: the few most recent, newest first.
 *
 * Not a total and not a count — an employee wants to know whether the dinner
 * they paid for has been dealt with, which is a list of two or three things.
 */
export const recentClaims = (claims = [], limit = 3) =>
  [...(claims || [])]
    .filter((c) => c && c.type === "ANY")
    .sort((a, b) => Date.parse(b.createdAt || 0) - Date.parse(a.createdAt || 0))
    .slice(0, limit)
    .map((c) => ({
      _id: c._id,
      label: c?.policyCateogary?.name || "Claim",
      amount: Number(c.amount) || 0,
      status: c.status,
      reimbursed: c.status === "approved" && Boolean(c.reimbursedAt || c.reimbursedInPayroll),
      submitted: dayMonth(c.createdAt),
    }));

/**
 * Who on the team is away, within the window.
 *
 * The book's card says "Nobody else in Finance is away before 1 April" when it
 * is empty — an absence of news is worth stating on a card about other people,
 * because a blank one reads as not loaded.
 */
export const awayThisWeek = (entries = [], days = 7, now = new Date(), selfId = null) => {
  const today = utcDay(now.toISOString());
  if (today == null) return [];
  return (entries || [])
    .filter((e) => {
      const id = String(e.employee?._id || e.employee || "");
      if (selfId && id === String(selfId)) return false;
      const from = utcDay(e.startDate || e.from);
      const to = utcDay(e.endDate || e.to) ?? from;
      if (from == null) return false;
      // Overlaps the window: started before it ends, ends after it starts.
      return from <= today + days * DAY_MS && (to == null || to >= today);
    })
    .map((e) => ({
      _id: e._id,
      name: `${e.employee?.fname || ""} ${e.employee?.lname || ""}`.trim() || "A colleague",
      until: dayMonth(e.endDate || e.to),
      type: e.leaveType?.name || e.leaveType || null,
    }));
};
